// Custom header behaviour (self-contained — does not depend on the theme's header.js):
//   1. Mobile drawer open/close + body scroll lock + accordions.
//   2. Inline expanding predictive search (click the bar -> it expands, the
//      Search button slides to the bottom-right, predictive results fill the panel).

/* ----------------------------- Mobile drawer ----------------------------- */

const OPEN_CLASS = 'uheader-drawer-open';
const SEARCH_OPEN_CLASS = 'uheader-search-open';

/* The homepage uses Lenis smooth-scroll, which scrolls via JS and ignores
 * `overflow: hidden`. So overlays must pause Lenis directly (and resume it
 * only once nothing else is holding the page locked). */
function pausePageScroll() {
  window.__bottleLenis?.stop();
}
function resumePageScroll() {
  const html = document.documentElement;
  if (html.classList.contains(OPEN_CLASS) || html.classList.contains(SEARCH_OPEN_CLASS)) {
    return;
  }
  window.__bottleLenis?.start();
}

function getDrawer() {
  return document.querySelector('[data-uheader-drawer]');
}

function openDrawer() {
  const drawer = getDrawer();
  if (!drawer) return;
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add(OPEN_CLASS);
  pausePageScroll();
  const focusable = drawer.querySelector('[data-uheader-close]');
  if (focusable) focusable.focus();
}

function closeDrawer() {
  const drawer = getDrawer();
  if (!drawer) return;
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(OPEN_CLASS);
  resumePageScroll();
}

function toggleAccordion(button) {
  const expanded = button.getAttribute('aria-expanded') === 'true';
  button.setAttribute('aria-expanded', String(!expanded));
  const panel = button.nextElementSibling;
  if (panel) panel.hidden = expanded;
}

/* --------------------------- Expanding search ---------------------------- */

const EXPANDED_CLASS = 'is-expanded';

function getSearch() {
  return document.querySelector('[data-uheader-search]');
}

function expandSearch() {
  const search = getSearch();
  if (!search || search.classList.contains(EXPANDED_CLASS)) return;
  search.classList.add(EXPANDED_CLASS);
  // Morph the shared surface so the card connects to the pill as one container.
  getBar()?.classList.add('is-search-open');
  // Lock the page behind the expanded search panel so it can't scroll away.
  document.documentElement.classList.add(SEARCH_OPEN_CLASS);
  pausePageScroll();
  const input = search.querySelector('.uheader__search-input');
  if (input) input.setAttribute('aria-expanded', 'true');
}

function collapseSearch() {
  const search = getSearch();
  document.documentElement.classList.remove(SEARCH_OPEN_CLASS);
  getBar()?.classList.remove('is-search-open');
  resumePageScroll();
  if (!search || !search.classList.contains(EXPANDED_CLASS)) return;
  search.classList.remove(EXPANDED_CLASS);
  const input = search.querySelector('.uheader__search-input');
  if (input) input.setAttribute('aria-expanded', 'false');
}

/* ------------------------------ Mega menu -------------------------------- */
// The header pill and the dropdown are one surface (.uheader__panel). Hovering a
// nav trigger adds .is-mega-open to the bar (grows its height) and shows the
// matching panel. Driven from JS so moving between the row and the panel — both
// inside the bar — never flickers the way a pure :hover chain would.

const MEGA_OPEN_CLASS = 'is-mega-open';
let megaCloseTimer;

function getBar() {
  return document.querySelector('[data-uheader-bar]');
}

function setActiveTrigger(bar, index) {
  bar.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
    const active = index != null && trigger.getAttribute('data-mega-trigger') === String(index);
    trigger.setAttribute('aria-expanded', String(active));
    trigger.closest('.uheader__nav-item')?.classList.toggle('uheader__nav-item--active', active);
  });
}

function openMega(index) {
  const bar = getBar();
  if (!bar) return;
  clearTimeout(megaCloseTimer);
  let active = null;
  bar.querySelectorAll('[data-mega-panel]').forEach((panel) => {
    const isActive = panel.getAttribute('data-mega-panel') === String(index);
    panel.hidden = !isActive;
    if (isActive) active = panel;
  });
  setActiveTrigger(bar, index);
  bar.classList.add(MEGA_OPEN_CLASS);
  // Grow the container to exactly the active panel's height — a plain height
  // transition, so the bar just gets taller to reveal the menu.
  const viewport = bar.querySelector('.uheader__mega-viewport');
  if (viewport && active) viewport.style.height = `${active.offsetHeight}px`;
}

function closeMega() {
  const bar = getBar();
  if (!bar || !bar.classList.contains(MEGA_OPEN_CLASS)) return;
  bar.classList.remove(MEGA_OPEN_CLASS);
  setActiveTrigger(bar, null);
  const viewport = bar.querySelector('.uheader__mega-viewport');
  if (viewport) viewport.style.height = '0px';
}

function initMega() {
  const bar = getBar();
  if (!bar || bar.__megaBound) return;
  bar.__megaBound = true;

  // Entering a trigger opens (or swaps to) its panel.
  bar.addEventListener('pointerover', (event) => {
    const trigger = event.target.closest('[data-mega-trigger]');
    if (trigger) openMega(trigger.getAttribute('data-mega-trigger'));
  });

  // Leaving the whole bar closes it after a short grace period; re-entering
  // (e.g. dipping into the panel) cancels the close.
  bar.addEventListener('pointerleave', () => {
    megaCloseTimer = setTimeout(closeMega, 140);
  });
  bar.addEventListener('pointerenter', () => clearTimeout(megaCloseTimer));

  // Keyboard: open on trigger focus, close once focus leaves the bar entirely.
  bar.addEventListener('focusin', (event) => {
    const trigger = event.target.closest('[data-mega-trigger]');
    if (trigger) openMega(trigger.getAttribute('data-mega-trigger'));
  });
  bar.addEventListener('focusout', () => {
    requestAnimationFrame(() => {
      if (!bar.contains(document.activeElement)) closeMega();
    });
  });
}

initMega();

/* ------------------------------- Wiring ---------------------------------- */

// Expand when the user types in the search field (covers keyboard users without
// auto-opening on programmatic focus / focus restored during navigation).
document.addEventListener('input', (event) => {
  if (event.target.closest('[data-uheader-search]')) expandSearch();
});

// Always start collapsed — including when the page is restored from the
// back/forward cache, which would otherwise keep it open after navigation.
// Also drop any focus the browser auto-restored to the lone search field, so
// the caret isn't sitting in it on load before the user chooses to search.
function blurSearchOnLoad() {
  const input = document.querySelector('.uheader__search-input');
  if (input && document.activeElement === input) input.blur();
}
window.addEventListener('pageshow', () => {
  collapseSearch();
  blurSearchOnLoad();
  requestAnimationFrame(blurSearchOnLoad);
});

document.addEventListener(
  'click',
  (event) => {
    const search = event.target.closest('[data-uheader-search]');

    // Clicks outside the search collapse it.
    if (!search) collapseSearch();

    // Drawer triggers.
    const opener = event.target.closest('[data-uheader-open]');
    if (opener) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-uheader-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const accordion = event.target.closest('[data-uheader-accordion]');
    if (accordion) {
      event.preventDefault();
      toggleAccordion(accordion);
      return;
    }

    // Search interactions.
    if (search) {
      // The × button closes the expanded panel.
      if (event.target.closest('[data-uheader-search-close]')) {
        event.preventDefault();
        collapseSearch();
        return;
      }

      // Collapsed: any click expands; a click on the Search button should expand
      // rather than submit. Once expanded, the Search button submits normally.
      if (!search.classList.contains(EXPANDED_CLASS)) {
        if (event.target.closest('.uheader__search-btn')) event.preventDefault();
        expandSearch();
        const input = search.querySelector('.uheader__search-input');
        if (input) input.focus();
      }
    }
  },
  true
);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  if (document.documentElement.classList.contains(OPEN_CLASS)) closeDrawer();
  collapseSearch();
  closeMega();
});

/* -------------------- Transparent-header overlay offset -------------------- */
// The header is transparent and floats over the content. On pages that overlay
// it (the homepage hero), the CSS pulls #MainContent up by --uheader-h. Measure
// the real header height so the pull is exact across breakpoints / logo sizes.
// Measure the row (.uheader__bar-main) so the shared surface can reserve exactly
// that height above the mega content — keeping the two seamless as the bar grows.
function setHeaderRowHeight() {
  const bar = getBar();
  const row = bar?.querySelector('.uheader__bar-main');
  if (!bar || !row) return;
  const rowHeight = Math.round(row.getBoundingClientRect().height);
  if (rowHeight > 0) bar.style.setProperty('--uheader-row-h', `${rowHeight}px`);
}

function measureHeader() {
  setHeaderOverlayHeight();
  setHeaderRowHeight();
}

function setHeaderOverlayHeight() {
  const section =
    document.querySelector('.uheader-section') || document.querySelector('[data-custom-header]');
  if (!section) return;
  const height = Math.round(section.getBoundingClientRect().height);
  if (height > 0) {
    document.documentElement.style.setProperty('--uheader-h', `${height}px`);
  }
}

measureHeader();
window.addEventListener('load', measureHeader);
window.addEventListener('resize', measureHeader);
window.addEventListener('pageshow', measureHeader);

// Re-measure once web fonts / the logo image settle (they change header height).
if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(measureHeader);
}
if ('ResizeObserver' in window) {
  const section =
    document.querySelector('.uheader-section') || document.querySelector('[data-custom-header]');
  // Observe the row, not the section, so the observer only fires on real layout
  // changes — not when the mega (an absolute overlay) expands on hover.
  const row = document.querySelector('.uheader__bar-main');
  if (section) new ResizeObserver(measureHeader).observe(section);
  if (row) new ResizeObserver(measureHeader).observe(row);
}
